<script context="module">
  // @ts-check
  /**
   * Array of valid container elements for the Image component.
   * @type {("figure" | "div" | "span")[]}
   */
  export const VALID_CONTAINER_ELEMENTS = ["figure", "div", "span"];
</script>

<script>
  // @ts-check
  import { fade } from "svelte/transition";
  import { validateAspectRatioClasses } from "../utilities/aspectRatios";
  import { validatePropValue } from "../utilities/logs";
  import { getTheme } from "../utilities";

  /**
   * The reference to the image container element.
   */
  export let imageContainerRef;

  /** Primarily used for testing
   * @type {string}
   *  */
  export let id = null;

  /**
   * The source URL of the image.
   * @type {string}
   */
  export let src;

  /**
   *
   * @type {string} srcset - The srcset attribute for the image.
   */
  export let srcset;

  /**
   *
   * @type {string} sizes - The sizes of the image.
   */
  export let sizes;

  /**
   * The alt text for the image, for accessibility.
   * @type {string}
   */
  export let alt;

  /**
   * The theme in the card
   * @type {import('../utilities/themes.js').StandardThemeName | undefined}
   */
  export let theme = undefined;

  /**
   * A string of space-separated aspect ratio Tailwind classes.
   * @type {string}
   * @default "aspect-4/3"
   */
  export let aspectRatioClasses = "aspect-4/3";
  validateAspectRatioClasses(aspectRatioClasses);

  /**
   * The class for the container element.
   * @type {string}
   */
  export let containerClass = "";

  /**
   * The container element for the image.
   * @type {typeof VALID_CONTAINER_ELEMENTS[number]}
   * @default "figure"
   */
  export let containerElement = "figure";
  validatePropValue({
    componentName: "Image",
    propName: "containerElement",
    propValue: containerElement,
    validPropValues: VALID_CONTAINER_ELEMENTS
  });

  /**
   * The class for the image element.
   * @type {string}
   */
  export let elementClass = "";

  /**
   * Whether to eager load the image.
   * @type {boolean}
   */
  export let eagerLoad = false;

  /**
   * The theme used for the image frame,
   * doesn't really need to be reactive,
   * but necessary to support theme switching in storybook at runtime.
   *
   * @type {string}
   */
  $: frameClasses = theme
    ? `border-[12px] lg:border-[14px] xl:border-[16px] ${getTheme(theme)?.borderColor}`
    : "";
</script>

<svelte:element
  this={containerElement}
  bind:this={imageContainerRef}
  class="{aspectRatioClasses} {containerClass}"
  {id}
>
  <img
    class="h-full w-full object-cover object-center {frameClasses} {elementClass} "
    {src}
    {alt}
    {srcset}
    {sizes}
    aria-hidden={alt === ""}
    loading={eagerLoad ? "eager" : "lazy"}
    in:fade
    data-chromatic="ignore"
  />
</svelte:element>
